import React, { useEffect, useState } from 'react';
import { NavLink, Link as RouterLink, useLocation } from 'react-router-dom';
import { links } from '../../Data';
import {
  FaWhatsapp,
  FaLinkedin,
  FaGithub,
  FaInstagram,
  FaTwitter,
  FaDownload,
} from 'react-icons/fa';
import { BsSun, BsMoon } from 'react-icons/bs';
import { Link as ScrollLink } from 'react-scroll';
import { animateScroll } from 'react-scroll';

function getStorageTheme() {
  return localStorage.getItem('theme') || 'light-theme';
}

function NavItems({ isHome, onNavigate, linkClassName }) {
  return links.map((link, index) => {
    const base = linkClassName || '';
    if (link.navType === 'route') {
      return (
        <li className="nav__item" key={index}>
          <NavLink
            to={link.path}
            className={({ isActive }) =>
              `${base}${isActive ? ' text-white after:scale-x-100' : ''}`
            }
            onClick={onNavigate}
          >
            {link.name}
          </NavLink>
        </li>
      );
    }

    if (!isHome) {
      return (
        <li className="nav__item" key={index}>
          <RouterLink
            to={`/#${link.path}`}
            className={({ isActive }) => `${base}${isActive ? ' text-white after:scale-x-100' : ''}`}
            onClick={onNavigate}
          >
            {link.name}
          </RouterLink>
        </li>
      );
    }

    return (
      <li className="nav__item" key={index}>
        <ScrollLink
          className={linkClassName}
          activeClass="text-white after:scale-x-100"
          to={link.path}
          spy
          smooth
          offset={-150}
          duration={500}
          onClick={onNavigate}
        >
          {link.name}
        </ScrollLink>
      </li>
    );
  });
}

const Header = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [scrollNav, setScrollNav] = useState(false);
  const [theme, setTheme] = useState(getStorageTheme);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const scrollTop = () => {
    animateScroll.scrollToTop();
  };

  const changeNav = () => {
    if (window.scrollY >= 80) {
      setScrollNav(true);
    } else {
      setScrollNav(false);
    }
  };

  const toggleTheme = () => {
    if (theme === 'light-theme') {
      setTheme('dark-theme');
    } else {
      setTheme('light-theme');
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', changeNav);
    return () => window.removeEventListener('scroll', changeNav);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', showMenu);
  }, [showMenu]);

  useEffect(() => {
    document.documentElement.className = theme;
    localStorage.setItem('theme', theme);
  }, [theme]);

  const closeMenu = () => setShowMenu(false);

  return (
    <header
      className={[
        'sticky top-0 z-[100] border-b border-white/10 bg-black/50 backdrop-blur',
        scrollNav ? 'bg-black/70' : '',
      ].join(' ')}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative h-16 flex items-center">
          {/* Left: Logo */}
          <RouterLink
            to="/"
            onClick={scrollTop}
            className="flex items-center gap-2 text-white font-semibold tracking-tight"
            aria-label="Yogesh home"
          >
            <span className="h-9 w-9 rounded-lg border border-white/10 bg-white/5 grid place-items-center text-xs font-bold">
              YS
            </span>
            <span className="text-[15px] leading-none">
              Yogesh<span className="text-accent">.</span>
            </span>
          </RouterLink>

          {/* Center: Nav (true centered) */}
          <nav className="hidden md:flex absolute left-1/2 -translate-x-1/2">
            <ul className="flex items-center gap-8 text-[13px] font-medium text-white/80">
              <NavItems
                isHome={isHome}
                onNavigate={closeMenu}
                linkClassName="relative py-2 transition-colors hover:text-white after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-accent after:transition-transform hover:after:scale-x-100 active:after:scale-x-100"
              />
            </ul>
          </nav>

          {/* Right: Actions */}
          <div className="ml-auto flex items-center gap-3">
            <a
              href={`${process.env.PUBLIC_URL || ''}/resume_4yrs.pdf`}
              download="Yogesh_Singh_Resume.pdf"
              className="hidden sm:inline-flex h-10 items-center gap-2 rounded-lg bg-accent px-4 text-[13px] font-semibold text-black shadow-soft transition-transform hover:scale-[1.02]"
            >
              <FaDownload />
              Resume
            </a>

            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="h-10 w-10 rounded-lg border border-white/10 bg-white/5 text-white grid place-items-center transition-colors hover:bg-white/10"
            >
              {theme === 'light-theme' ? <BsMoon /> : <BsSun />}
            </button>

            <button
              type="button"
              className="md:hidden h-10 w-10 rounded-lg border border-white/10 bg-white/5 text-white grid place-items-center hover:bg-white/10"
              onClick={() => setShowMenu(!showMenu)}
              aria-label="Toggle menu"
              aria-expanded={showMenu}
            >
              <span className="sr-only">Menu</span>
              <div className="flex flex-col gap-1">
                <span className="h-[2px] w-5 bg-white/90" />
                <span className="h-[2px] w-5 bg-white/90" />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {showMenu && (
        <>
          <button
            className="fixed inset-0 bg-black/60 backdrop-blur-sm md:hidden"
            onClick={closeMenu}
            aria-label="Close menu"
          />
          <div className="md:hidden fixed top-16 left-0 right-0 border-b border-white/10 bg-black/80 backdrop-blur">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <ul className="flex flex-col gap-3 text-sm font-medium text-white/85">
                <NavItems
                  isHome={isHome}
                  onNavigate={closeMenu}
                  linkClassName="py-2 transition-colors hover:text-white"
                />
              </ul>

              <div className="mt-4 flex items-center gap-3">
                <a
                  href={`${process.env.PUBLIC_URL || ''}/resume_4yrs.pdf`}
                  download="Yogesh_Singh_Resume.pdf"
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-accent px-4 text-[13px] font-semibold text-black shadow-soft w-full"
                  onClick={closeMenu}
                >
                  <FaDownload />
                  Resume
                </a>
              </div>

              <div className="mt-4 flex items-center gap-4 text-white/80">
                <a href="https://wa.link/br0lsr" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                  <FaWhatsapp />
                </a>
                <a href="https://www.linkedin.com/in/yogesh1dev" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <FaLinkedin />
                </a>
                <a href="https://github.com/yogeshmlv" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                  <FaGithub />
                </a>
                <a href="https://www.instagram.com/its__novo/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <FaInstagram />
                </a>
                <a href="https://twitter.com/Yogesh__Dev" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <FaTwitter />
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
